import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';

/**
 * Komunikacja z klientem — CS (prace 26.10.2026, pakiet P1; decyzja
 * użytkownika 26.10.2026: CS według makiety p. Mariusza). Klient API
 * `apps/api/src/client-communication/`. **Z aplikacji nic nie wychodzi**:
 * szkic do skopiowania albo otwarcia w swojej poczcie, potem „Wysłane
 * ręcznie” — oświadczenie CS z autorem i godziną.
 */
export const FEATURE_CLIENT_AVIZATIONS = 'clientAvizations';
export const FEATURE_CLIENT_UPDATES = 'clientUpdates';
export const FEATURE_CLIENT_QUESTIONS = 'clientQuestions';

/** Kanały awizacji — kody z API (dane), napisy jak w API. */
export const AVIZATION_CHANNELS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'phone', name: 'telefon' },
  { id: 'email', name: 'mail' },
  { id: 'platform', name: 'platforma klienta' },
  { id: 'excel', name: 'plik Excel klienta' },
];

export const AVIZATION_POINTS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'loading', name: 'załadunek' },
  { id: 'unloading', name: 'rozładunek' },
];

export const AVIZATION_STATES: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'todo', name: 'do wykonania' },
  { id: 'done', name: 'wykonane' },
  { id: 'skipped', name: 'pominięte' },
  { id: 'superseded', name: 'zastąpione nowszą' },
  { id: 'all', name: 'wszystkie' },
];

/** Wyniki rozmowy — jak wyniki awizacji w sekcji 7 (`csWorkApi`). */
export const PHONE_RESULTS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'confirmed', name: 'potwierdzono termin' },
  { id: 'change_requested', name: 'prośba o zmianę terminu' },
  { id: 'no_contact', name: 'brak kontaktu — ponowić' },
  { id: 'other', name: 'inny wynik' },
];

export const RULE_POINTS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'loading', name: 'załadunek' },
  { id: 'unloading', name: 'rozładunek' },
  { id: 'both', name: 'załadunek i rozładunek' },
];

export const UPDATE_CATEGORIES: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'vehicle', name: 'zmiana pojazdu' },
  { id: 'carrier', name: 'przewoźnik (wykonawca)' },
  { id: 'schedule', name: 'zmiana terminu w TMS' },
  { id: 'order_change', name: 'zaakceptowana zmiana zlecenia' },
  { id: 'cancellation', name: 'anulowanie zlecenia' },
];

/** Gotowe powody pominięcia (makieta) — każdy ma co najmniej 10 znaków. */
export const SKIP_REASONS = [
  'odbiorca nie wymaga awizacji',
  'awizowano innym kanałem',
  'klient nie wymaga tej informacji',
];

export const MIN_REASON = 10;

export interface Avization {
  id: number;
  userOfferId: number;
  orderNumber: string;
  clientName: string | null;
  point: string;
  pointLabel: string;
  channel: string;
  channelLabel: string;
  dueAt: string;
  origin: string;
  originLabel: string;
  originNote: string | null;
  state: string;
  stateLabel: string;
  overdue: boolean;
  attempts: number;
  lastResult: string | null;
  lastResultLabel: string | null;
  note: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  doneAt: string | null;
  doneByName: string | null;
  skipReason: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface AvizationDetail extends Avization {
  subject: string | null;
  body: string | null;
  bodyEdited: boolean;
  template: string | null;
  fields: Array<{ label: string; value: string | null }>;
  phoneScript: string;
  canWrite: boolean;
}

export interface ContactOption {
  source: 'order' | 'contractor';
  name: string | null;
  phone: string | null;
  email: string | null;
}

export interface ClientUpdate {
  id: number;
  userOfferId: number;
  orderNumber: string;
  clientName: string | null;
  category: string;
  categoryLabel: string;
  eventText: string;
  eventAt: string;
  ruleState: string;
  ruleStateLabel: string;
  state: string;
  stateLabel: string;
  overdue: boolean;
  recipient: string | null;
  subject: string;
  body: string;
  bodyEdited: boolean;
  template: string;
  sentAt: string | null;
  sentByName: string | null;
  skipReason: string | null;
  skippedByName: string | null;
  createdAt: string;
}

export interface ClientQuestion {
  id: number;
  preOrderId: number;
  fields: string[];
  fieldLabels: string[];
  state: string;
  stateLabel: string;
  overdue: boolean;
  recipient: string | null;
  subject: string;
  body: string;
  bodyEdited: boolean;
  template: string;
  sentAt: string | null;
  sentByName: string | null;
  answerText: string | null;
  answeredAt: string | null;
  answeredByName: string | null;
  cancelReason: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface RuleView {
  rule: {
    config: Record<string, unknown>;
    owner: string | null;
    validFrom: string;
    inForce: boolean;
    updatedByName: string | null;
    updatedAt: string;
  } | null;
  history: Array<{
    id: number;
    oldConfig: Record<string, unknown> | null;
    newConfig: Record<string, unknown> | null;
    oldOwner: string | null;
    newOwner: string | null;
    validFrom: string | null;
    reason: string;
    changedByName: string | null;
    changedAt: string;
  }>;
  canWrite: boolean;
}

/** Filtr z adresu listy (`?filter={…}` z kafelka pulpitu) — tylko napisy i `true`. */
export function filterFromSearch(search: string): Record<string, string | boolean> {
  try {
    const raw = new URLSearchParams(search).get('filter');
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, string | boolean> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string' || v === true) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

// ── Awizacje ─────────────────────────────────────────────────────────────

export async function fetchAvizations(params: {
  state?: string;
  channel?: string;
  day?: string;
}): Promise<{ items: Avization[]; total: number; reminderMin: number }> {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => (v ?? '') !== ''));
  const { data } = await axiosNest.get('/client-avizations', { params: clean });
  return { items: data?.items ?? [], total: data?.total ?? 0, reminderMin: data?.reminderMin ?? 60 };
}

export async function fetchAvization(id: number | string): Promise<AvizationDetail> {
  const { data } = await axiosNest.get<AvizationDetail>(`/client-avizations/${id}`);
  return data;
}

export async function fetchOrderAvizations(
  userOfferId: number | string,
): Promise<{ items: Avization[]; canWrite: boolean; contacts: ContactOption[] }> {
  const { data } = await axiosNest.get(`/user-offers/${userOfferId}/client-avizations`);
  return { items: data?.items ?? [], canWrite: !!data?.canWrite, contacts: data?.contacts ?? [] };
}

export async function createAvization(
  userOfferId: number | string,
  body: {
    point: string;
    channel: string;
    dueAt: string;
    contactName?: string;
    contactPhone?: string;
    contactEmail?: string;
    note?: string;
  },
): Promise<Avization> {
  const { data } = await axiosNest.post<Avization>(`/user-offers/${userOfferId}/client-avizations`, body);
  return data;
}

export async function savePhoneResult(id: number, body: { result: string; text?: string }): Promise<AvizationDetail> {
  const { data } = await axiosNest.post<AvizationDetail>(`/client-avizations/${id}/phone-result`, body);
  return data;
}

export async function saveAvizationDraft(
  id: number,
  body: { subject: string; body: string; recipient?: string },
): Promise<AvizationDetail> {
  const { data } = await axiosNest.put<AvizationDetail>(`/client-avizations/${id}/draft`, body);
  return data;
}

export async function markAvizationDone(id: number, text?: string): Promise<AvizationDetail> {
  const { data } = await axiosNest.post<AvizationDetail>(`/client-avizations/${id}/done`, text ? { text } : {});
  return data;
}

export async function skipAvization(id: number, reason: string): Promise<AvizationDetail> {
  const { data } = await axiosNest.post<AvizationDetail>(`/client-avizations/${id}/skip`, { reason });
  return data;
}

export async function repeatAvization(id: number, reason: string): Promise<AvizationDetail> {
  const { data } = await axiosNest.post<AvizationDetail>(`/client-avizations/${id}/repeat`, { reason });
  return data;
}

// ── Aktualizacje dla klienta ─────────────────────────────────────────────

export async function fetchClientUpdates(state?: string): Promise<{ items: ClientUpdate[]; total: number }> {
  const { data } = await axiosNest.get('/client-updates', { params: state ? { state } : {} });
  return { items: data?.items ?? [], total: data?.total ?? 0 };
}

export async function fetchOrderClientUpdates(userOfferId: number | string): Promise<ClientUpdate[]> {
  const { data } = await axiosNest.get(`/user-offers/${userOfferId}/client-updates`);
  return data?.items ?? [];
}

export async function saveClientUpdate(
  id: number,
  body: { subject: string; body: string; recipient?: string },
): Promise<ClientUpdate> {
  const { data } = await axiosNest.put<ClientUpdate>(`/client-updates/${id}`, body);
  return data;
}

export async function markClientUpdateSent(id: number): Promise<ClientUpdate> {
  const { data } = await axiosNest.post<ClientUpdate>(`/client-updates/${id}/sent`, {});
  return data;
}

export async function skipClientUpdate(id: number, reason: string): Promise<ClientUpdate> {
  const { data } = await axiosNest.post<ClientUpdate>(`/client-updates/${id}/skip`, { reason });
  return data;
}

// ── Pytanie do klienta ───────────────────────────────────────────────────

export async function fetchClientQuestions(preOrderId: number | string): Promise<{
  items: ClientQuestion[];
  missingFields: Array<{ key: string; label: string }>;
  suggestedRecipient: string | null;
  canWrite: boolean;
}> {
  const { data } = await axiosNest.get(`/pre-orders/${preOrderId}/client-questions`);
  return {
    items: data?.items ?? [],
    missingFields: data?.missingFields ?? [],
    suggestedRecipient: data?.suggestedRecipient ?? null,
    canWrite: !!data?.canWrite,
  };
}

export async function createClientQuestion(
  preOrderId: number | string,
  body: { fields: string[]; recipient?: string },
): Promise<ClientQuestion> {
  const { data } = await axiosNest.post<ClientQuestion>(`/pre-orders/${preOrderId}/client-questions`, body);
  return data;
}

export async function saveClientQuestion(
  id: number,
  body: { subject: string; body: string; recipient?: string },
): Promise<ClientQuestion> {
  const { data } = await axiosNest.put<ClientQuestion>(`/client-questions/${id}`, body);
  return data;
}

export async function markClientQuestionSent(id: number): Promise<ClientQuestion> {
  const { data } = await axiosNest.post<ClientQuestion>(`/client-questions/${id}/sent`, {});
  return data;
}

export async function answerClientQuestion(id: number, text: string): Promise<ClientQuestion> {
  const { data } = await axiosNest.post<ClientQuestion>(`/client-questions/${id}/answer`, { text });
  return data;
}

export async function cancelClientQuestion(id: number, reason: string): Promise<ClientQuestion> {
  const { data } = await axiosNest.post<ClientQuestion>(`/client-questions/${id}/cancel`, { reason });
  return data;
}

// ── Reguły klienta ───────────────────────────────────────────────────────

export type RuleKind = 'avization' | 'update';

const rulePath = (contractorId: number | string, kind: RuleKind) =>
  `/contractors/${contractorId}/${kind === 'avization' ? 'avization-rule' : 'update-rule'}`;

export async function fetchRule(contractorId: number | string, kind: RuleKind): Promise<RuleView> {
  const { data } = await axiosNest.get<RuleView>(rulePath(contractorId, kind));
  return data;
}

export async function saveRule(
  contractorId: number | string,
  kind: RuleKind,
  body: { config: Record<string, unknown> | null; owner?: string | null; validFrom?: string; reason: string },
): Promise<RuleView> {
  const { data } = await axiosNest.put<RuleView>(rulePath(contractorId, kind), body);
  return data;
}

/** Opis reguły słowami — do historii i nagłówka. */
export function ruleSummary(kind: RuleKind, config: Record<string, unknown> | null): string {
  if (!config) return 'brak reguły';
  if (kind === 'avization') {
    const channel = AVIZATION_CHANNELS.find((c) => c.id === config.channel)?.name ?? String(config.channel);
    const points = RULE_POINTS.find((p) => p.id === config.points)?.name ?? String(config.points);
    return `${channel}, ${points}, ${String(config.leadHours)} h przed oknem`;
  }
  const cats = Array.isArray(config.categories) ? (config.categories as string[]) : [];
  if (cats.length === 0) return 'klient nie chce informacji';
  return cats.map((c) => UPDATE_CATEGORIES.find((u) => u.id === c)?.name ?? c).join(', ');
}

export { toErrorMessage };
